/// <reference types="jquery" />

import { ManagerSet } from "./ResourceManager";
import { LoaderConfig, ObjectLoader } from "./ObjectLoader";
import { MaterialManager } from "./Material";
import { Logger, LogLevel } from "./Logger";
import { Utils } from "./Utils";
import { ShaderLoader } from "./ShaderLoader";
import { MaterialSet } from './data/MaterialSet';
import { Navigator } from "./Navigator";
import { GUI } from "./GUI";
import { Data } from "./Data";
import { Property, UniformSetter, PropertyType, PropertySet, RuleSet } from "./data/Property";
import { Slots } from "./Slots";
import { ItemConfig, PropertyDetail } from './ItemConfig';
import { Configuration } from './Configuration';

import * as _ from "lodash";
import * as THREE from "three";
import { Object3D, Camera, Vector3 } from "three";

import '../exports/app';

import { PropertyOption} from "./data/PropertyOption";
import { App3DConfig, App3DConfigInt } from "./config/App3DConfig";
import { RenderContext} from "./RenderContext";
import { Assignment, Rule } from "./utils/General";
import { ComposedLoadingProgress } from "./LoadingProgress";
import { ViewConfig, ViewConfigInt } from "./config/ViewConfig";

// re-export to be used in API module
export { ItemConfig, PropertyDetail } from './ItemConfig';
export { RenderContext} from "./RenderContext";
export { Navigator } from "./Navigator";
export { App3DConfig } from "./config/App3DConfig";
export { ViewConfig } from "./config/ViewConfig";

/**
 * Main API access point.
 *
 * [Download declaration files](../assets/download/typings.zip)
 */
export class App3D {
	/**
	 * @hidden
	 */
	public view: JQuery;
	protected $loader: JQuery;

	// data containers
	// basic object cache - all loaded objects are stored here
	protected objects: { [key:string]:THREE.Object3D } = {} as any;
	protected loaders: {} = {} as any;
	protected materials: {} = {} as any;
	// empty Object3Ds which serves as parent to components
	protected slotAnchors: { [key:string]:THREE.Object3D } = {};
	// actual component objects - this is kept across whole application run, so switching to another object will update slots to previously selected components
	protected slotComponents: { [key:string]:THREE.Object3D } = {};
	// keeps track of slots of each model
	protected model_slots: { [key:string]:Slots } = {};
	protected shared_models: {} = {} as any;

	protected static texSetCnt: number = 0;

	protected smManagerSet: ManagerSet; // encapsulates loading of multiple resources type into one loading manager
	// Element containing "active" mesh (for developement only)
	protected _meshselect: JQuery;

	protected textures: {} = {} as any;
	protected timestamp: number;

	protected baseScale: number = 1.0;
	protected baseRotation: number = 0;

	// THREE entities
	protected renderer: THREE.WebGLRenderer;
	protected _turntable: THREE.Object3D = null;
	protected _anchor: THREE.Object3D = null;
	protected _scene: THREE.Scene = null;
	protected _camera: THREE.PerspectiveCamera = null;
	protected _center: THREE.Vector3; // look-at target

	// manager
	protected manager: THREE.LoadingManager;

	// THREE loaders
	protected textureLoader: THREE.TextureLoader;
	protected textureLoaderC: THREE.CubeTextureLoader;

	// custom loaders
	protected objectLoader: ObjectLoader;
	protected _matManager: MaterialManager;

	protected logger: Logger;
	// Console3D object - dev only
	protected console3d: any;
	protected _navigator: Navigator;

	/** Data of currently loaded product */
	protected _data: Data.ProductData;
	protected active_product: Data.Product;
	protected product_is_ready: boolean = false;
	protected view_config: Object = {};

	protected product: string;
	protected properties: {[key:string]:string|number} = {};

	protected _gui:GUI;

	public config: App3DConfig;

	protected pendingDebugRequest = false;

	protected active = true;
	/**
	 * @hidden
	 * Materials, which are sensitive to view angle
	 */
	protected angleSensitiveMaterials: MaterialSet[] = [];

	protected debugEnv = false;

	/**
	 * @hidden
	 * Keep material cached, so previous settings (shader uniforms) are kept
 	 */
	protected materialCache: {[key:string]:MaterialSet} = {};

	// Uniforms related tu model transformations (sizing)
	protected _sizeUniformSet: Data.UniformSet;

	// Basic data loaded - ready to interaction
	protected _initialized: Promise<void> = null;

	// Everything is loaded
	protected _productLoaded: Promise<void>;

	// Constructor requests are finished - unifies initialization and product loading (if configured)
	protected _ready: Promise<void[]>;

	// List of functions to be called when debug button is pressed
	// This allow easy dumping of local variables
	protected _debugCallbacks: (() => void)[] = [];

	/**
	 * @hidden
	 */
	public $: JQueryStatic;

	/**
	 * Initializes 3D preview with configurator.
	 * @param container JQuery selector of main container (render element)
	 * @param data Product data in JSON format (see [[App3D.loadProductData]])
	 * @param options Configurator options (see [[App3DConfig]])
	 * @param renderContext
	 */
	constructor(container:string, data: object = null, options:object = {}, renderContext: RenderContext = null) {
		this.config = new App3DConfigInt(options, this.view);
		let readyPromises:Promise<void>[] = [];
		this.$ = this.config.jQuery
			? this.config.jQuery
			: $;

		this._initialized = new Promise((resolve) => {

			this.view = this.$(container);
			this.view.addClass('App3D-view');

			this.view_config = this.config.view_config;
			this._gui = new GUI(this, this.config.gui_container);

			this.logger = new Logger(this, LogLevel.Debug, '-');
			this.logger.showLocation = true;

			THREE.Cache.enabled = !this.config.disable_cache;

			this._center = new THREE.Vector3(0, -this.config.floor, 0);
			this.manager = new THREE.LoadingManager();
			// this.manager.onProgress = (item, loaded, total) => this.logger.log(item + ': (' + loaded + '/' + total + ')', 'load');
			this.$loader = this.config.progress_bar ? this.$(this.config.progress_bar) : null;
			if (this.$loader){
				let $prog = this.$loader.find('.progress-bar');
				this.showLoader();
				this.manager.onProgress = (item, loaded, total) => {
					$prog.css('width', Math.round(100 * total/loaded)+'%');
				}
			}
			/*
			else
				this.manager.onProgress = (item, loaded, total) => console.log(item + ': (' + loaded + '/' + total + ')', 'load');*/
			this.manager.onLoad = () => {
				this.showLoader(false);
				this.logger.log('Loading complete', 'load');
			};

			this.textureLoader = new THREE.TextureLoader(this.manager);
			this.textureLoaderC = new THREE.CubeTextureLoader(this.manager);
			this.objectLoader = new ObjectLoader(this);
			this.textureLoader.setPath(this.config.texture_path);
			this.textureLoaderC.setPath(this.config.texture_path);
			this.objectLoader.setPath(this.config.model_path);

			this.textureLoader.setCrossOrigin('annonymous');
			this.textureLoaderC.setCrossOrigin('annonymous');

			let vw = this.view.outerWidth();
			let vh = this.view.outerHeight();
			let ratio = (vw && vh)
				? (vw / vh)
				: 1;
			let cDist = 10;

			if (renderContext){
				this._camera = renderContext.camera;
				this._scene = renderContext.scene;
				this._anchor = renderContext.anchor;
			}
			else {
				this._camera = new THREE.PerspectiveCamera(30, ratio, 0.1, 1000); // allways 1 for square
				this._camera.position.set(0, cDist, -cDist);
				this._camera.lookAt(this._center);
				this._scene = new THREE.Scene();
				this._anchor = new THREE.Object3D();
				// this._anchor.rotation.order = 'XZY';
			}

			this._turntable = new THREE.Object3D();
			this._turntable.add(this._anchor);
			this._scene.add(this._turntable);
			this._scene.add(this._camera);

			this.renderer = new THREE.WebGLRenderer({
				alpha: true,
				antialias: true,
				preserveDrawingBuffer: true, // to allow screenshots
			});
			this.renderer.setPixelRatio(window.devicePixelRatio);

			if (this.config.background){
				if (this.config.background === 'transparent')
					this.renderer.setClearColor(0xffffff, 0.0);
				else
					this.renderer.setClearColor(new THREE.Color(this.config.background).getHex());
			}
			else
				this.renderer.setClearColor(0xffffff, 0.0);

			this.view.append(this.renderer.domElement);

			let navigator = this.config.navigator;
			if (navigator && (navigator instanceof Navigator)) {
				navigator.setCamera(this._camera, this._center);
				navigator.setAnchor(this._turntable);
				this._navigator = navigator;
			}
			else{
				this._navigator = new Navigator({
					container: this.config.navigator_area,
					autorotate: false
				}, this.$, this);
				if (navigator){
					this._navigator.setConstraints(new Utils.Options(navigator));
				}
			}
			// this._navigator.panStart();

			this._navigator.onChange((navigator:Navigator) => {
				for (let materialSet of this.angleSensitiveMaterials)
					materialSet.each((material) => {
						material.uniforms['fViewAngle'].value = navigator.elevation;
					});
			});

			this.updateAnchorTransforms(new Utils.Transform());

			if (this.console3d) {
				this.console3d.setCenter(this._center);
				this.console3d.add3DEditor('Camera', this._camera);
				this.console3d.addValueEditor('Phi', 'phi');
				this.console3d.addValueEditor('PhiL', 'p2');
			}

			this._matManager = new MaterialManager(this, {
				autoExt: 'png',
				texAuto: MaterialManager.TEX_AO | MaterialManager.TEX_SPECULAR
			});
			this._matManager.load('Pad', {color: 0xcccccc});

			this.smManagerSet = new ManagerSet(this, {
				objects: this.objectLoader,
				textures: this.textureLoader,
				texturesC: this.textureLoaderC,
				shaders: new ShaderLoader(this.manager, this.config.shader_path)
			});

			this._sizeUniformSet = new Data.UniformSet();

			this.$(window).resize(() => {
				this.updateSize();
			});

			this._gui.attachGUIEvents();
			this.updateSize();

			this.timestamp = Date.now();
			this.renderLoop();
			resolve();
		});

		readyPromises.push(this._initialized);
		if (data){
			readyPromises.push(new Promise((resolve, reject) => {
				this._initialized.then(() => {
					this.loadProductData(data).then(() => {
						if (this.config.preload_models) // noinspection JSIgnoredPromiseFromCall (couldnt care less)
							this.preloadProductAssets();
						resolve();
					}, reject);
				}, reject);
			}));
		}

		this._ready = Promise.all(readyPromises);
	}

	/**
	 * Loads product data using URL
	 * @param productURL
	 */
	public loadProduct(productURL:string): Promise<void> {
		return new Promise((resolve, reject) => {
			let dataURL = this.config.app_root+ 'rest/product/' + productURL;
			this.$.get(dataURL, (data) => {
				this.loadProductData(data).then(resolve, reject);
			}, 'json')
			.fail(() => {
				reject(new Error(`Loading [${dataURL}] has failed!`));
			});
		});
	}

	/**
	 * Loads product family data.
	 * @param data data to be loaded - as returned by REST api ```"/product/"```
	 * @return {Promise} Default product variant is fully loaded
	 */
	public loadProductData(data: object): Promise<void>{
		this.product_is_ready = false;
		this._productLoaded = new Promise((resolve, reject) => {
			this._data = new Data.ProductData(data);
			this.active_product = new Data.Product(this.data.products[this.data.active_product]);
			if (this.data.view_config)
				Object.assign(this.view_config, this.data.view_config);

			for (let prop in this.data.properties)
				if (this.data.properties.hasOwnProperty(prop)) {
					// validates current value for each property (and sets default value for uninitialized ones)
					this.properties[prop] = this.data.properties[prop].getValidValue(
						this.properties.hasOwnProperty(prop)
							? String(this.properties[prop])
							: null,
						this.active_product.code
					);
				}

			try{
				this.properties = this.getVariantPropertyValues(
					new PropertySet(this.data.properties, this.properties),
					this.data.active_product);

				for (let prop in this.properties){
					// update option listing
					this.gui.updatePropertyOptionListing(prop, this.getPropertyOptionsNow(prop));
				}
			}
			catch (e) {
				console.error(e);
			}

			this.setProductVariant(this.data.active_product)
				.then(() => {
					// assign default materials
					let promises:Promise<void>[] = [];
					_.each(this.data.materials_default, (sData) => {
						promises.push(this.setMaterial(new Rule(sData.target, this.getModelMaterial(sData.value))));
					});
					this.attachProductEvents();
					Promise.all(promises).then(() => {
						this.product_is_ready = true;
						resolve();
					}, reject);
				}, reject);
		});
		return this._productLoaded;
	}

	getLoadProgress(): ComposedLoadingProgress{
		return this.smManagerSet.progress;
	}

	/**
	 * Find valid property value to product (or all product within family)
	 * @param currentPropertyValues
	 * @param product ID of product (all family products will be examined if not set)
	 */
	protected getVariantPropertyValues(currentPropertyValues: PropertySet, product: string = null):{[key:string]:string|number} {
		let newProperties = null;	// reserve for perfect match

		let candidateDivergence = Number.POSITIVE_INFINITY;
		let candidateConditions: RuleSet = null;
		_.each(this.data.catalog, (catalogItem:{}) => {
			if (catalogItem.hasOwnProperty('product')	// only loop through products
				&& (!product || (catalogItem['product'] == product))){ // product match (if product set
				if (!catalogItem.hasOwnProperty('conditions')){
					newProperties = currentPropertyValues.getPropertyObject();
					return false;	// no need to continue, if we found perfect match
				}
				let conditionSet = new RuleSet(catalogItem['conditions']);
				// calculate divergence of variant possible values to current ones
				let variantDivergence = conditionSet.divergence(currentPropertyValues);
				if (variantDivergence < candidateDivergence){	// set new candidate, if
					candidateConditions = conditionSet;
					candidateDivergence = variantDivergence;
				}
			}
		});

		if (!(newProperties || candidateConditions))
			throw new Error('No variants at all');

		return newProperties
			// Current property values are already matching product data
			? newProperties
			// find new set of property values, which are matching candidate rules
			: candidateConditions.getPropertySet(this.data.properties, currentPropertyValues).getPropertyObject();
	}

	// noinspection JSUnusedGlobalSymbols - Developement
	/**
	 * Adds helper objects for anchor debugging purposes
	 */
	protected addHelpers(){
		this._anchor.traverse((object: THREE.Object3D) => {
			if ((object instanceof THREE.Skeleton) || (object instanceof THREE.SkinnedMesh)){
				let helper = new THREE.SkeletonHelper( object );
				if (helper.material instanceof THREE.LineBasicMaterial){
					helper.material.linewidth = 2;
					helper.material.color.set(0xffff00);
				}
				this._scene.add( helper );
			}
		});

		this._anchor.traverse((bone) => {
			if ((bone instanceof THREE.Bone)){
				bone.add( new THREE.AxisHelper( 2 ) );
				console.log(`helper Added for ${bone.name}`);
			}
		});
	}

	protected frame(offset) {
	}

	/**
	 * @hidden
	 * Main anchor
	 * @return {Object3D}
	 */
	get anchor(): THREE.Object3D{
		return this._anchor;
	}

	/**
	 * @hidden
	 * Main turntable
	 * @return {Object3D}
	 */
	get turntable(): THREE.Object3D{
		return this._turntable;
	}

	/**
	 * @hidden
	 * Camera object
	 * @return {Camera}
	 */
	get camera(): THREE.Camera{
		return this._camera;
	}

	/**
	 * @hidden
	 * Camera target
	 * @return {Vector3}
	 */
	get center(): THREE.Vector3{
		return this._center;
	}

	/**
	 * Navigator object
	 * @return {Navigator}
	 */
	get navigator(): Navigator{
		return this._navigator;
	}

	/**
	 * @hidden
	 */
	get gui(): GUI{
		return this._gui;
	}

	/**
	 * @hidden
	 */
	get data(): Data.ProductData{
		if (!this._data)
			throw new Error('No product is loaded yet');
		return this._data;
	}

	/**
	 * Shows or hides GUI progress bar / loader
	 * @param {boolean} status
	 */
	protected showLoader(status = true){
		if (!this.$loader)
			return;
		if (status)
			this.$loader.show();
		else
			this.$loader.hide();
	}

	/**
	 * Loads model(s) to application (app.models)
	 * @param modelIDs
	 * @param onLoad onLoad callback
	 * @param anchor parent to append loaded model to
	 * @param slots
	 */
	protected appendModels(modelIDs:string[], onLoad:(data:any, slots:Slots)=>void, anchor:THREE.Object3D = null, slots:Slots = null): Promise<void> {
		return new Promise((resolve, reject) => {
			let dataLoad:{ [key:string]:string } = {};
			let dataCached:{ [key:string]:THREE.Object3D } = {};
			let ready = true;
			for (let mID of modelIDs){
				// check whether object is already cached in "objecsts" cache
				if (!this.objects.hasOwnProperty(mID)) {
					if (!this.data.model_index.hasOwnProperty(mID)) {
						console.warn('Cannot append unindexed object: ' + mID);
					}
					dataLoad[mID] = this.data.model_index[mID];
					ready = false;
				}
				else{
					dataCached[mID] = this.objects[mID];
					if (anchor)
						anchor.add(this.objects[mID]);
				}
			}

			if (ready) { // already loaded
				if (onLoad) onLoad(dataCached, slots);
				resolve();
				return;
			}
			this.showLoader();
			// need to load
			this.smManagerSet.get(
				{
					objects: dataLoad
				},
				(data) => {
console.log(`this.smManagerSet.get`);
					_.each(data.objects, (mData, mID) => {
						if (!mData)
							console.warn('No data:' + mID);
						else {
							this.objects[mID] = mData;
							if (anchor)
								anchor.add(this.objects[mID]);
						}
					});
					this.showLoader(false);
					if (onLoad) onLoad(data.objects, slots);
					resolve();
				},
				'model',
				slots);
		});
	}

	/**
	 * Updates slot with new model (or just clears it if no new model set)
	 * @param {string} slot
	 * @param {Object3D} newVal
	 */
	protected setSlot(slot:string, newVal:THREE.Object3D = null):void {
console.log(`slot(${slot}, ${newVal ? newVal.name : '<null>'})`);
		let slotObject: THREE.Object3D;
		if (slot == '#')
			slotObject = this._anchor;
		else
			if (this.slotAnchors.hasOwnProperty(slot))
				slotObject = this.slotAnchors[slot];
			else { // automatically create new slot on main achnor
				console.warn(`Adding implicit slot [${slot}]`);
				slotObject = new THREE.Object3D();
				this._anchor.add(slotObject);
				this.slotAnchors[slot] = slotObject;
			}
				// return;	// nothing to update
		// Detach all children from slot
		while (slotObject.children.length > 0)
			slotObject.remove(slotObject.children[0]);
		if (newVal)	// add new object
			slotObject.add(newVal);
	};

	/**
	 * @hidden
	 * Gets currently selected product.
	 * @return {Data.Product}
	 */
	getProduct():Data.Product{
		return this.active_product;
	}

	// noinspection JSUnusedGlobalSymbols - API export
	/**
	 * Determines, whether there is fully loaded product in application
	 * Use onProductReady if you want promise.
	 * @return Application has fully loaded model (is initialized and not loading another one).
	 */
	onReady(): Promise<void[]>{
		return this._ready;
	}

	/**
	 * @return {Promise} Application is ready
	 */
	onInitialized(): Promise<void>{
		return this._initialized;
	}

	/**
	 * @return {Promise} All product assets are loaded
	 */
	onProductReady(): Promise<void>{
		return new Promise<void>((resolve, reject) => {
			this._ready.then(() => {
				if (!this._productLoaded)
					reject(new Error("Product can't be ready, if you didn't even bothered to load it! Try loadProductData() first."));
				this._productLoaded.then(resolve, reject);
			});
		});
	}

	/**
	 * Sets product part model to slot (or clears slot, if empty).
	 * @param {string} slot
	 * @param {string} value new model ID, null to clear
	 */
	protected setPart(slot:string, value:string) {
		return new Promise((resolve, reject) => {
			// switches slot in current _scene, when everything is ready
			let setSlot = (object:THREE.Object3D = null) => {
				if (object)
					this.slotComponents[slot] = object;	// save component to loaded components
				this.setSlot(slot, object);
				resolve();
			};

			// callback is either directly setslot, delayed by timeout (effect)
			let callBack = this.config.effect_duration
				? (object:THREE.Object3D = null) => {
					setTimeout(() => {
						setSlot(object);
					}, this.config.effect_duration / 2); // change part in the middle of effect
				}
				: setSlot;

			if (value)
				this.appendModels(
					[value],
						(data) =>	{
						callBack(data[value]);
					}
				);
			else
				callBack();
		});
	}

	getCurrentFamily(): string{
		return this.data
			? this.data.meta['family']
			: null;
	}

	/**
	 * Switches to another product variant within family.
	 * @param {string} product Product code
	 * @return {Promise} All product resources (models and textures) are set
	 */
	setProductVariant(product:string): Promise<void> {
console.log(`setProductVariant(${product})`);
		return new Promise((resolve, reject) => {
console.log(`setProductVariant(${product}) promises...`);
			let promises:Promise<void>[] = [];
			if (!this.data.products.hasOwnProperty(product))
				reject(new Error(`Currently loaded product data for [${this.getCurrentFamily()}] does not contain requested product [${product}]`));
			let productData:Data.Product = this.data.products[product];

			this.product = product;
			this.active_product = productData;
			// todo optimize, so models which are to be loaded are not removed at the first place
			_.each(productData.properties, (mData, mID) => {
				this._anchor.remove(mData);
				delete this.shared_models[mID];
			});
console.log(`setProductVariant(${product}) setSlot()...`);
			this.setSlot('#');	// clear achnor
console.log(`cycle properties...`);
			// Append slots which was previously removed
			_.each(productData.properties, (p_att) => {
				let prop = this.data.properties[p_att];
				if (!(prop.hasOwnProperty('set_models') && Object.keys(prop.set_models).length))
					return; // skip non-model properties, as they will be loaded after models via updateShaders()
				let p_val = this.gui.propertyValue(p_att);
				if (!p_val)
					p_val = String(this.properties[p_att]);
				if (p_val)
					promises.push(this.setPropertyNow(p_att, p_val));
			});
console.log(`load slots ...`);
			if (!this.model_slots.hasOwnProperty(productData.model))
				this.model_slots[productData.model] = new Slots();
console.log([productData.model, ...productData.shared_parts]);
// return;
			this.appendModels(
				this.$.merge([productData.model], productData.shared_parts),
				(models, slots:Slots) => {
					_.each(productData.shared_parts, (mID) => {
						this.shared_models[mID] = this.objects[mID];
					});
					// Slots will not be reused, because every model has slots on different location
					// Remove slots from previous model
					for (let slot in this.slotAnchors){
						this._anchor.remove(this.slotAnchors[slot]);
						delete this.slotAnchors[slot];
					}
					// Create new slots
					for (let slot of slots.keys()){
						this.slotAnchors[slot] = slots.get(slot);
						this._anchor.add(this.slotAnchors[slot]);
					}

					this.active_product = new Data.Product(productData);

					if (!this.data.products[product].hasOwnProperty('view_config'))
						this.data.products[product].view_config = new ViewConfig();

					// Update application config from product or global configuration
					// Read and set up properties
					let viewConfig:ViewConfigInt = new ViewConfigInt();

					viewConfig.append(this.data.products[product].view_config);
					viewConfig.append(this.config.view_config);

					// Perform actual update
					if (viewConfig.navigator)
						this._navigator.setConstraints(new Utils.Options(viewConfig.navigator), true);

					this.updateSlots();	// occupy newly initialized anchors with components, if available
					this.updateShaders();

					_.each(productData.properties, (p_att) => {
						let prop = this.data.properties[p_att];
						if (!(prop.hasOwnProperty('set_models') && Object.keys(prop.set_models).length))
							return; // skip non-model properties, as they will be loaded after models via updateShaders()
						let $p_in = this.$('input[name="' + p_att + '"]:checked');
						let p_val = $p_in.length
							? $p_in.val()
							: prop.defaultValue;
						if (p_val)
							promises.push(this.setPropertyNow(p_att, p_val));
					});

					this.updateAnchorTransforms(new Utils.Transform(viewConfig.transform ? viewConfig.transform : null));
				},
				this._anchor,
				this.model_slots[productData.model])
.then(() => {
	console.log(`model [${product}] is appended with following slots`, this.model_slots[productData.model]);
});
			Promise.all(promises).then(() => { resolve(); }, reject);
		});
	}

	protected updateAnchorTransforms(transform: Utils.Transform){
		this.baseScale = this.config.model_scale * transform.scale;
		this._anchor.scale.setScalar(this.baseScale);
		this._anchor.position.fromArray(transform.offset);
		this._anchor.position.y += this.config.floor;

		this._anchor.rotation.x = transform.rotate[0] * Math.PI / 180;
		this._anchor.rotation.y = transform.rotate[1] * Math.PI / 180;
		this._anchor.rotation.z = transform.rotate[2] * Math.PI / 180;
	}

	/**
	 * Assigns each available components to proper slot (if present on current product)
	 */
	protected updateSlots():void {
		for (let slot in this.slotAnchors)
			this.setSlot(slot,
				this.slotComponents.hasOwnProperty(slot)
					? this.slotComponents[slot]
					: null);
	}

	/**
	 * @hidden
	 * @internal
	 * Returns loader configuration corresponding to selected format.
	 * Encapsulates THREE loader and all flags tied to loading from specified format (mesh fixes etc...).
	 * @param id format ID
	 */
	getLoader(id: string): LoaderConfig {
		// cached loader
		if (this.loaders.hasOwnProperty(id))
			return this.loaders[id];

		let ldata = new LoaderConfig();
		switch (id) {
			case 'json':
				ldata.loader = new THREE.JSONLoader(this.manager);
				ldata.fixTransform = true;
				// ldata.mirrorUVs = true;
				ldata.createMesh = true;
				break;
			case 'json_ex':
				ldata.loader = new THREE.ObjectLoader(this.manager);
				ldata.fixTransform = true;
				// ldata.mirrorUVs = true;
				ldata.replaceMaterial = new THREE.Material();
				ldata.replaceMaterial.visible = false;
				break;
				// case 'obj':
				// ldata.loader = new THREE.OBJLoader(this.manager);
				// ldata.createMesh = false;
				// break;
			case 'dae':
				ldata.loader = new THREE.ColladaLoader();
				break;
			default:
				return null;
		}

		if (!this.config.enable_materials)
			ldata.replaceMaterial = null;

		ldata.loader.onLoadComplete = () => {
			this.logger.log('Loading [' + id + '] done');
			_.each(this.objects, (value) => {
				this._scene.add(value);
			});
		};
		this.loaders[id] = ldata;
		return ldata;
	}

	/**
	 * @hidden
	 * @internal
	 * Returns ThreeJS material based on material name.
	 * @param {string} materialID ID of material
	 * @return {Material}
	 */
	getMaterial(materialID: string): THREE.Material{
		return this.config.enable_materials
			? this._matManager.get(materialID)
			: null;
	}

	protected getTexture(filename, callback, loader = null):void {
		if (!filename)
			return;
		let tHash = Utils.hash(filename);
		if (!loader)
			loader = Array.isArray(filename) ? this.textureLoaderC : this.textureLoader;
		if (this.textures.hasOwnProperty(tHash)) {
			this.logger.log('Reusing: ' + filename, 'info');
			callback(this.textures[tHash])
		}
		loader.setPath(this.config.texture_path);
		loader.load(
			filename,
			(texture) => {
				this.textures[tHash] = texture;
				this.logger.log('Loaded: ' + filename, 'load');
				callback(this.textures[tHash])
			},
			(xhr) => {
				this.logger.log('Progress: ' + filename + ': (' + (xhr.loaded + ' / ' + xhr.total) + ')', 'load');
			},
			(xhr) => {
				this.logger.log('Error loading texture: ' + filename, 'error');
				this.logger.log(xhr, 'error');
			});
	}

	protected getTextureSet(textures, callback) {
		let setID = App3D.texSetCnt;
		App3D.texSetCnt++;
		let man = new THREE.LoadingManager();
		man.onProgress = function (item, loaded, total) {};
		let ldr = new THREE.TextureLoader(man);
		let ldc = new THREE.CubeTextureLoader(man);
		let data = {} as any;
		_.each(textures, (filename, id) => {
			this.getTexture(filename, (texture) => {
				this.setTextureWrapping(id, texture);
				data[id] = texture;
			}, Array.isArray(filename) ? ldc : ldr);
		});
		man.onLoad = () => {
			this.logger.log('Textures set [' + setID + '] loaded', 'load');
			callback(data);
		};
	}

	protected tick(){
		let newTimestamp = Date.now();
		this.frame(newTimestamp - this.timestamp);
		this.timestamp = newTimestamp;
	}

	protected renderFrame(){
		this.tick();
		if (this.pendingDebugRequest){
			this.pendingDebugRequest = false;
			console.debug('Debug request (scene):');
			console.debug(this._scene);
			this.navigator.dumpState();

			this._anchor.traverse((mesh:THREE.Object3D) => {
				if (mesh instanceof THREE.Mesh){
					console.debug('Mesh: '+mesh.name);
					if (mesh.geometry instanceof THREE.Geometry){
						console.debug('Geometry colors:', mesh.geometry);
						for (let col of mesh.geometry.colors)
							console.debug(col.getHexString());
					}
					if (mesh.geometry instanceof THREE.BufferGeometry){
						console.debug('BufferGeometry colors:');
						if (mesh.geometry.attributes.hasOwnProperty('color')){
							let colors = mesh.geometry.attributes['color'];
							for (let v = 0; v < colors.array.length; v += 3)
								console.debug(new THREE.Color(colors.array[v], colors.array[v+1], colors.array[v+2]).getHexString());
						}
					}
				}
			});
		}

		this.renderer.render(this._scene, this._camera);
	}

	/**
	 * Animates and renders app frame
	 */
	protected renderLoop() {
		if (!this.active)
			return;
		this.renderFrame();
		requestAnimationFrame(() => {
			this.renderLoop();
		});
	}

	/**
	 * Loads material data and applies it to model
	 * All required resources are loaded automatically
	 * @param matDef Material definition
	 * @param {(mesh) => boolean} condition callback to determine whether material should be applied or not
	 * @param cbQueue
	 */
	private applyNewMaterial(matDef:Data.Material, condition: (mesh: any) => boolean, cbQueue:Utils.CbQueue) {
		if(!this.config.enable_materials) return;
		// use existing material when cached
		if (this.materialCache.hasOwnProperty(matDef.prototypeID)){
			this._anchor.traverse((obj) => this.applyShaderMaterial(obj, condition, this.materialCache[matDef.prototypeID]));
			if (cbQueue)  // Run next callback from queue
				cbQueue.next();
		}
		else
			this.smManagerSet.get(matDef.data, (data) => {
				this.setTexturesWrapping(data.textures);
				let materialSet:MaterialSet = new MaterialSet(
					data.shaders,
					this.$.extend({}, data.textures, data.texturesC),
					matDef.hasOwnProperty('uniforms')
						? matDef.uniforms
						: {}, this._sizeUniformSet, matDef.prototypeID);

				this._anchor.traverse((obj) => this.applyShaders(obj, condition, materialSet));
				if (cbQueue)  // Run next callback from queue
					cbQueue.next();
			}, matDef.name);
	}

	/**
	 * Sets material to _scene objects by specified rule
	 * @param {Rule} rule rule to apply
	 * @param {Utils.CbQueue} cbQueue List of functions to be called when materials are set
	 * @return {Promise} When all materials set and textures loaded
	 */
	private setMaterial(rule:Rule, cbQueue:Utils.CbQueue = null):Promise<void> {	// must be private, so we don't have to export Rule class
		return new Promise((resolve, reject) => {
			if (!cbQueue)	// we need queue
				cbQueue = new Utils.CbQueue();
			cbQueue.add(resolve);
			if (rule.selector.indexOf(':') === -1) { // assign just by material name
				this.applyNewMaterial(rule.material, (mesh) => {
					return mesh.userData['material'] === rule.selector;
				}, cbQueue);
			}
			else { // advanced conditions
				let cond_list = [];
				_.each(rule.selector.split(','), (selector) => {
					let data = selector.trim().split(':');
					let property = 'material';
					let value = data[0];
					if (data.length > 1) {
						property = data[0];
						value = data[1];
					}
					let cnd = (value.indexOf('*') > -1)
						? '.match(/' + value.replace(/[\-\[\]\/\{\}\(\)\+\?\.\\\^\$\|]/g, "\\$&").replace(/\*/g, '.*') + '/)'
						: '=="' + value + '"';
					let cmp = null;
					switch (property) {
						case 'material':
							cmp = 'mesh.userData.material';
							break;
						case 'mesh':
							cmp = 'mesh.name';
							_.each(this.data.per_mesh_textures, (pm_set) => {
								if (this.$.inArray(value, pm_set.selectors) > -1) {
									rule.material.textures = this.$.extend(rule.material.textures, pm_set.textures);
								}
							});
							break;
					}
					cond_list.push('(' + cmp + cnd + ')');
				});
				let cb = Function('mesh', 'return ' + cond_list.join(' || ') + ';');
				this.applyNewMaterial(rule.material, (mesh) => {
					return cb(mesh);
				}, cbQueue);
			}
		});
	}

	/**
	 * Set texture mapping by their ID
	 * @param textures
	 */
	protected setTexturesWrapping(textures) {
		_.each(textures,  (tData, tID) =>{
			this.setTextureWrapping(tID, tData);
		});
	}


	// noinspection JSMethodCanBeStatic
	protected setTextureWrapping(tID, texture) {
		if (tID !== 'AO') {
			texture.wrapS = THREE.RepeatWrapping;
			texture.wrapT = THREE.RepeatWrapping;
			texture.repeat.set(4, 4);
		}
	}

	/**
	 * Update view (canvas) size from <view> element. Call when size of container element changes.
	 * @param vw Viewport width (default is configured view width)
	 * @param vh Viewport Height (default is configured view height)
	 */
	public updateSize(vw:number = null, vh:number = null):void {
		if ((vw === null) || (vh === null)){
			vw = this.view.outerWidth();
			vh = this.view.outerHeight();
		}
		if (!(vw && vh))
			console.warn(`Invalid viewport size: ${vw}×${vh}`);
		this.renderer.setSize(vw, vh);
		this._camera.aspect = (vw / vh);
		this._camera.updateProjectionMatrix();
	}

	protected animateAnchor(){
		// this.animateForthAndBack((size) => {
		// 	this._anchor.scale.setScalar(this.baseScale * size);
		// });
		let cnt = 5; // number of waves
		let qScale = 0.2; // size of wobble

		return new Promise((resolve, reject) => {
			this.$({t: 1}).animate({t : 0}, {
				step: (t) => {
					let tScale = (1-t) * cnt;
					let sizeDiff = Math.sin(tScale * Math.PI);
					let sizeScale = (1-t) * qScale;
					this._anchor.scale.setScalar(this.baseScale * (1 + sizeDiff * sizeScale));
					// https://gist.github.com/gre/1650294
					let ts = t<.5 ? 2*t*t : -1+(4-2*t)*t; // quad
					// let ts = t<.5 ? 4*t*t*t : (t-1)*(2*t-2)*(2*t-2)+1; //cubic
					this._navigator.setRotationOffset(this.baseRotation + (ts * 360));
				},
				easing: 'linear',
				duration: this.config.effect_duration,
				complete: resolve
			});
		});
	}

	/**
	 * Sets property value.
	 * Only works after product is loaded.
	 * @param {string} property Property name
	 * @param {string} value New property value
	 * @return {Promise}
	 */
	public setProperty(property:string, value:string|number): Promise<void> {
		return new Promise((resolve, reject) => {
			if (!this._productLoaded)
				reject(new Error('No product!'));
			this._productLoaded.then(() => {
				this.setPropertyNow(property, value).then(resolve, reject);
			});
		});
	}

	/**
	 * Sets property value without waitng to product load
	 * @param {string} property Property name
	 * @param {string} value New property value
	 * @return {Promise}
	 */
	protected setPropertyNow(property:string, value:string|number): Promise<void> {
		return new Promise((resolve, reject) => {
			this._initialized.then(() => {
				this.properties[property] = value;
				let prop: Property = this.data.properties[property];
				switch (prop.type) {
					case PropertyType.Color:
						this.setColorProperty(prop, <string>value);
						resolve(); // do not wait
						return;
					case PropertyType.Number:
						this.setNumberProperty(prop, Number(value));
						resolve(); // do not wait
						return;
					default:
						try {
							this.setChoiceProperty(prop, <string>value, property).then(() => { resolve(); }, reject);
						}
						catch (e) {
							reject(e);
						}
				}
			});
		});
	}

	// only when this._initialized
	protected setColorProperty(property:Property, value: string){
		if (!Object.keys(property.set_uniforms).length){
			console.warn(`Color property ${property} has no set_uniform rules!`);
			return;
		}
		if (!value.match(/^#?[a-z0-9]{6}$/))
			throw new Error(`Invalid color value: ${value}`);

		this._anchor.traverse((mesh: THREE.Mesh) => {
			if (mesh instanceof THREE.Mesh)
				_.each(property.set_uniforms, (setVal: string, selector: string) => {
					let material: string;
					let uniform: string;
					[, material, uniform] = selector.split(':');
					if (mesh.userData['material'] === material){
						if (!(mesh.material instanceof THREE.ShaderMaterial))
							console.warn(`${material} is not shader material`);
						else if (!mesh.material.uniforms.hasOwnProperty(uniform))
							console.warn(`${material} is has no uniform named ${uniform}`);
						else {
							if (!value.match(/^#/))
								value = '#'+value;
							mesh.material.uniforms[uniform] = new THREE.Uniform(new THREE.Color(value));
						}
					}
				});
		});
	}

	// only when this._initialized
	protected setNumberProperty(property:Property, value: number){
		if (!Object.keys(property.cb_position).length){
			console.warn(`Number property ${property} has no set_position rules!`);
			return;
		}

		let n_val = Number(value);
		_.each(property.cb_position, (callback:UniformSetter) => {
			// [name] = selector.split(':'); // this._sizeUniformSet.size may be replaced depending on name
			/*
			This will apply user property formula to entered value and set-ups uniform
			 */
			callback(this._sizeUniformSet.size, n_val);
		});
	}

	// only when this._initialized
	protected setChoiceProperty(property: Property, value: string, propName): Promise<void[]> {
		let promises:Promise<void>[] = [];
		if (this.config.effect_duration)
			// noinspection JSIgnoredPromiseFromCall
			this.animateAnchor();

		if (value && (value != property.getValidValue(String(value))))
			throw new Error('Value is not defined: ' + property + '[' + value + ']');

		if (Object.keys(property.set_models).length) {
console.debug(`setChoiceProperty(${value})`,property.set_models);
			let sID = 0;
			_.each(property.set_models, (value, selector) => {
				promises.push(this.setPart(
					selector,
					this.getRealValue(propName, value)).then(
						() => { this.updateShaders(); }
					));
				sID++;
			});
		}	// updateShaders
		else if (Object.keys(property.set_materials).length){
			// this.app3DConfig.enable_materials = true;	// do not auto-enable
			this.updateShaders();
		}
		return Promise.all(promises);
	}

	/**
	 * Determines, whether certain property is numeric or not
	 * @param {string} property Property name
	 * @return {boolean} Whether property is numeric or not
	 */
	propertyIsNumeric(property: string): boolean {
		return Object.keys(this.data.properties[property].cb_position).length > 0;
	}

	/**
	 * Wraps accessing product data from outside into promise after product is loaded
	 * @param callback
	 */
	protected whenLoaded(callback:() => any): Promise<any>{
		return new Promise((resolve, reject) => {
			this._ready.then(() => {
				if (!this._productLoaded)
					reject(new Error('No product!'));
				this._productLoaded.then(() => {
					try{
						resolve(callback());
					}
					catch (e) {
						reject(e);
					}
				}, reject);
			}, reject);
		});
	}

	/**
	 * Lists all properties for current product
	 * @return {Promise<string[]>} List of compatible options
	 */
	getProperties():Promise<string[]>{
		return this.whenLoaded(() => {
			return this.getPropertiesNow();
		});
	}

	protected getPropertiesNow():string[]{
		if (!this.data)
			throw new Error(`No product is loaded yet`);
		return Object.keys(this.data.properties);
	}

	/**
	 * Lists all available options of property for current product
	 * @param {string} prop Property ID
	 * @return {Promise<string[]>} List of compatible options
	 */
	getPropertyOptions(prop: string):Promise<string[]>{
		return this.whenLoaded(() => {
			return this.getPropertyOptionsNow(prop);
		});
	}

	/**
	 * Lists all available options of property for current product
	 * @param prop
	 */
	protected getPropertyOptionsNow(prop){
		let matchingOptions:string[] = [];
		if (!this.data)
			throw new Error(`No product is loaded yet`);

		if (!this.data.properties.hasOwnProperty(prop))
			throw new Error(`Unknown product property: [${prop}]`);

		// Find product-compatible property options
		for (let opt in this.data.properties[prop].optionsAll)
			if (this.data.properties[prop].optionsAll[opt].match(this.active_product.code))
				matchingOptions.push(opt);
		return matchingOptions;
	}

	/**
	 * Property option object containing label, price etc...
	 * @param property
	 * @param value
	 */
	getPropertyOption(property:string, value:string):Promise<PropertyOption>{
		return this.whenLoaded(() => {
			return this.getPropertyOptionNow(property, value);
		});
	}

	protected getPropertyOptionNow(property:string, value:string):PropertyOption{
		if (!this.data.properties.hasOwnProperty(property))
			throw new Error(`Invalid property: ${property}`);
		if (!this.data.properties[property].optionsAll.hasOwnProperty(value))
			throw new Error(`Invalid property value: ${property}[${value}]`);
		return this.data.properties[property].optionsAll[value];
	}
	/**
	 * Update shader for whole model by currently selected options
	 */
	protected updateShaders():void {
		if (!this.config.enable_materials) return;
		let assignmentList: { [selector:string]: Assignment} = {};
		let setRules: { [selector:string]: Rule} = {};
		let c = 0;

		_.each(this.data.materials_default, (sData) => {
			assignmentList[sData.target] = new Assignment(c++, this.getModelMaterial(sData.value));
		});

		_.each(this.data.properties, (pConfig, pName) => {
			_.each(pConfig.set_materials, (pVal, selector) => {
				let real_value = this.getRealValue(pName, pVal);
				if (real_value)
					assignmentList[selector] = new Assignment(c++, this.getModelMaterial(real_value));
			});
		});
// order unique rules
		_.each(assignmentList, (data, selector) => {
			// setRules[selector] = new Rule(selector, assignment.material);
			setRules[data.id] = new Rule(selector, data.material);
		});

		// list of callback which will set material by selector
		let cbList = new Utils.CbQueue();
		_.each(setRules, (rule: Rule) =>
			cbList.add(() => {
				this.setMaterial(rule, cbList);
			})
		);
		cbList.next();
	}

	/**
	 * Attaches all relevant events to GUI elements.
	 */
	protected attachProductEvents(): void{
		if (this.active_product){
			this._gui.attachProductMenuEvents();
			this._gui.attachPropertyEvents();
		}
	}

	/**
	 * Gets virtual property value - allows user to set property value by custom value
	 * @param {string} prop_name
	 * @param {string} value
	 * @param {() => void} onFail
	 * @return {null}
	 */
	protected getVirtualValue(prop_name:string, value:string, onFail: () => void){
		switch (prop_name+value){
			case 'connection@Addon':
				switch (this.getRealValue('color', '@')){
					case '9016':
						return 'AddonWhite';
					default:
						return 'AddonChrome';
				}
		}
		onFail();
		return null;
	}

	/**
	 * Calculates real value of property after alias translation
	 * @param prop_name Property name
	 * @param value Original property value
	 * @return {string} final value (can be null)
	 */
	protected getRealValue(prop_name:string, value:string):string {	// maybe public?
		let cur_val = this.properties[prop_name];
		if (!this.data.properties.hasOwnProperty(prop_name)){
			return this.getVirtualValue(prop_name, value,
				() => console.warn('Trying to read value of [%s]:%s on undefined property! (%O)', prop_name, value, this.data.properties));
		}

		let cur_opt:PropertyOption = <PropertyOption>this.data.properties[prop_name].getOption(String(cur_val));
		let r_limit = 10;
		while (r_limit-- > 0){
			let m = value.match(/^@(\w*)/); // value is not reference => return:
			if (!(m && m[1])) {
				if (!cur_opt)
					return this.getVirtualValue(prop_name, value,
						() => console.warn('Referencing value [%s]:%s on undefined property! (%O)', prop_name, value, this.data.properties));
				return m ? cur_opt.realValue : value; // when value is '@', return input value, else return selected option value
			}
			else {
				if (cur_opt && cur_opt.hasOwnProperty('vars') && cur_opt.vars.hasOwnProperty(value)) // reference to option variable
					value = cur_opt.vars[value];
				else if (this.data.properties.hasOwnProperty(m[1])){  // reference to another property
					cur_val = this.properties[m[1]];
					cur_opt = <PropertyOption>this.data.properties[m[1]].getOption(String(cur_val));
					value = cur_opt.realValue; // value set by option
				}
				else	// value not found - fallback to null
					return this.getVirtualValue(prop_name, value,
						() => console.warn('Property value not found [%s]: [%s]', prop_name, value));
			}
		}
		console.warn('Property [%s]:%s recursion limit reached!', prop_name, value);
		return null;
	}

	/**
	 * Returns material tailored to currently selected product - general material textures are replaced by model-specific ones
	 * @param {string} id Material ID
	 * @return {Data.Material}
	 */
	protected getModelMaterial(id:string):Data.Material {
		if (!this.active_product)
			console.error('Trying to get model material without product set!');

		if (!this.data.materials.hasOwnProperty(id))
			throw 'Material is not defined: '+id;

		let mMaterial = new Data.Material(
			this.data.materials[id],
			id,
			// only processed shaders are loaded with variants
			this.data.materials[id].shaders.vertex.match(/^shader\/general\.vert/)
				? ShaderEnv.FlagFullSizing
				: 0);

		let textures = {};
		_.each(mMaterial.textures, (v, k) => {
			let m = v.match(/(\w+):(\w+\.\w+)/);
			if (m) switch(m[1]){
				case 'product':
					textures[k] = this.active_product.textures[m[2]];
					break;
			}
		});
		Object.assign(mMaterial.textures, textures);
		return mMaterial;
	}

/**
 * Determines all values matched poperty real value
 */
	protected propertyValues(property:Property, value:string):string[]{
		if (property.hasOwnProperty('options') && property.options.hasOwnProperty(value))
			return [value];	// option id match
		if (property.hasOwnProperty('groups')){
			if (property.groups.hasOwnProperty(value))
				return [value];	// group id match
			for (let group in property.groups)
				if (property.groups.hasOwnProperty(group)){
					let match = this.propertyValues(property.groups[group], value);
					if (match.length){
						match.push(group);
						return match;
					}
				}
		}
		return [];
	}

	// noinspection JSUnusedGlobalSymbols - used by external application
	/**
	 * Get currently selected product-property combination as catalog item ID.
	 * @return {string} ID of catalog item
	 */
	public getCatalogProduct():string{
		let propertyValues: {[key:string]:string[]} = {};

		// get all valid values for each currently selected property
		// so when user select "material-A" from group "group-1", both ["material-A", "group-1"] are recognized as active values for property
		for (let prop in this.properties)
			if (this.properties.hasOwnProperty(prop))
				propertyValues[prop] = this.data.properties[prop].propertyValues(String(this.properties[prop]));
		// search whole catalog
		for (let cID in this.data.catalog)
			// only products are examined
			if (this.data.catalog[cID].product && (this.data.catalog[cID].product == this.product)){
				// return product catalog item if no conditions
				if (!this.data.catalog[cID].conditions)
					return cID;

				let cMatch = true;
				// test match for each condition
				for (let prop in this.data.catalog[cID].conditions)
					if (!(propertyValues.hasOwnProperty(prop) && (propertyValues[prop].indexOf(this.data.catalog[cID].conditions[prop]) >= 0)))
						cMatch = false;
				if (cMatch)
					return cID;
			}

		let debug = Object.entries(this.properties).map(([k, v]):string => { return k+':'+v; });

		for (let cID in this.data.catalog)
				// only products are examined
			if (this.data.catalog[cID].product && (this.data.catalog[cID].product == this.product)) {
				console.warn(`Using default product [${this.product}] config: [${debug.join(', ')}] (catalog item for current combination not found)`);
				return cID;
			}

		console.warn(`Missing product: ${this.product} [${debug.join(', ')}]`);
		return null;
	}

	/**
	 * Gets current product configuration (for adding to basket).
	 * @return {ItemConfig}
	 */
	public getCurrentConfig(): ItemConfig {
		if (!this.active_product)
			return null;

		let curProduct: Data.Product = this.active_product;
		let accessories: { [aID:string]: string} = {};
		let curConfig: {} = {};
		// create current configuration
		_.each(curProduct.properties, (pID) => {
			let pVal = this.properties[pID];
			curConfig[pID] = this.data.properties[pID].getOption(String(pVal));
			accessories[pID] = curConfig[pID].id;
		});

		let prodArticle = null;
		let propDetail:{ [key:string]: PropertyDetail} = {};
		_.each(this.data.catalog, (artConfig, artID) => {
			// find articles which matches product ID
			if (artConfig.hasOwnProperty('product') && (artConfig.product === curProduct.code)) {
				if (!artConfig.hasOwnProperty('conditions')) {
					// no restriction -> return first match and exit loop
					prodArticle = artID;
					return false;	// exit outer loop - matched first article with ID
				}
				else {
					// Check catalog item conditions
					let match = true;
					propDetail = {};
					_.each(artConfig.conditions, (pVal, pID) => {
						// try to match property to current config ID or one of aliases
						if ((curConfig[pID].id != pVal) && !(curConfig[pID].hasOwnProperty('alias') && (this.$.inArray(pVal, curConfig[pID].alias) >= 0))){
							match = false;	// mismatch
							return false; // exit inner loop - testing conditions
						}
						else
							propDetail[pID] = this.findProperty(this.data.properties[pID], curConfig[pID].id);
					});
					if (match) {  // entry was found
						prodArticle = artID;
						return false; // exit outer loop - searchning for matching catalog article
					}
				}
			}
		});

		return new ItemConfig(this.data.catalog, prodArticle, accessories, propDetail, this.data.meta);
	}

	// noinspection JSUnusedGlobalSymbols - API export
	/**
	 * Loads rest of product variant and accessory assets to cache.
	 * This only works for models for now.
	 * @return {Promise} All available assets are pre-loaded.
	 */
	preloadProductAssets(): Promise<void[]> {
		console.info('Pre-loading assets ...');
		let cacheLoaders:Promise<void>[] = [
			this.preloadProductModels()
		];

		return Promise.all(cacheLoaders);
	}

	protected preloadProductModels(): Promise<void> {
		return new Promise((resolve, reject) => {
			this.smManagerSet.get(
				{
					objects: this.data.model_index
				},
				(data) => {
					_.each(data.objects, (object, id) => {
						this.objects[id] = object;
					});
					resolve();
				},
				'model-preload');
		});
	}

	// noinspection JSUnusedGlobalSymbols - API export
	/**
	 * Gets configuration of currently selected product - all product properties are returned either as value or null if no value set
	 */
	getProductConfiguration(): Configuration {
		let config = new Configuration();
		for (let property in this.data.properties)
			config.set(property, this.properties.hasOwnProperty(property)
					? String(this.properties[property])
					: null);
		return config;
	}

	protected findProperty(property, value): PropertyDetail{
		if (!property.hasOwnProperty('options'))
			console.error('No Options! %o', property);
		if (property.options.hasOwnProperty(value))
			return new PropertyDetail(property.options[value]);	// found in options
		for (let gID in property.groups)
			if (property.groups.hasOwnProperty(gID)){
				let find = this.findProperty(property.groups[gID], value);	// found in sub-group
				if (find)
					return find;
			}
		return null;
	}

	/**
	 * @hidden
	 * Applies (already loaded) shaders to object
	 * @param obj root object to parse
	 * @param condition Callback which takes Mesh as parameter and based on its properties returns whether shader should be applied or not
	 * @param materialSet Material to be applied to meshes (material with uniforms compatible to mesh BufferAttributes is selected)
	 */
	protected applyShaders(
			obj: THREE.Object3D,
			condition: (mesh) => boolean,
			materialSet: MaterialSet) {
		if (materialSet.angleSensitive)
			this.angleSensitiveMaterials.push(materialSet);
		this.applyShaderMaterial(obj, condition, materialSet);
	}

	/**
	 * @hidden
	 * Apply existing materials to objects based on condition
 	 */
	protected applyShaderMaterial(
		obj: THREE.Object3D,
		condition: (mesh) => boolean,
		matAssign: MaterialSet
	){
		obj.traverse((mesh) => {
			if ((mesh instanceof THREE.Mesh) && condition(mesh))
				matAssign.applyMaterial(mesh);
		});
	}

	/**
	 * Updates shaders with texture loading
	 * Used mainly by shader dev tool as textures for shaders are not defined in material.
	 * @param obj
	 * @param condition
	 * @param shaders
	 * @param tex_files
	 * @param uniforms uniform data loaded from input field
	 */
	protected applyShadersTex(obj, condition: (mesh) => boolean, shaders, tex_files, uniforms) {
		this.getTextureSet(tex_files, (textures) => {
			this.applyShaders(obj, condition, new MaterialSet(shaders, textures, uniforms, this._sizeUniformSet, 'texShader#'));
		});
	}

	/**
	 * @hidden
	 * @internal
	 * Dumps application state to console (calls _debugCallbacks)
	 */
	debugState():void {
		console.log('App state debug: ');
		for (let callback of this._debugCallbacks)
			callback();
	}

	/**
	 * @hidden
	 * @internal
	 * Requests debug dump for next render frame
	 */
	debugRequest():void {
		this.pendingDebugRequest = true;

		if (!this.debugEnv){
			// Add debug objects aswell
			this.debugEnv = true;
			// for (let i = this._anchor.children.length - 1; i >= 0; i--)
			// 	this._anchor.remove(this._anchor.children[i]);
			this._anchor.add(new THREE.AxisHelper( 1 ));
			this._anchor.add(new THREE.GridHelper(1, 5, 0x00ff00, 0xcfcfcf));

			this._turntable.add(new THREE.GridHelper(1, 5, 0x00ff00, 0xcfcfcf));

			this.$('body').keypress(( event ) => {
				console.log(event);
				let o = event.shiftKey
					? Math.PI / 8
					: 0.1;
				let prop = event.shiftKey
					? this._anchor.rotation
					: this._anchor.position;

				switch (event.key){
					case 'ArrowLeft':
						prop.x -= o;
						break;
					case 'ArrowRight':
						prop.x += o;
						break;
					case 'ArrowUp':
						prop.y -= o;
						break;
					case 'ArrowDown':
						prop.y += o;
						break;
					case 'PageUp':
						prop.z -= o;
						break;
					case 'PageDown':
						prop.z += o;
						break;
				}
				console.log(`New ${event.shiftKey ? 'rot' : 'pos'}: [${prop.x}, ${prop.y}, ${prop.z}]`);
			});
		}
	}

	/**
	 * Returns currently rendered 3D scene as image dataURL.
	 */
	getCurrentFrame(): string{
		return this.renderer.domElement.toDataURL('image/png');
	}

	/**
	 * Returns current video frame as image dataURL.
	 */
	getCurrentVideo(): string { return null; }

	/**
	 * Takes application screenshot.
	 * Result contains images as dataURL:
	 * ```
	 * {
	 * 	render: <dataURL>,
	 * 	video?: <dataURL>
	 * }
	 * ```
	 * @param doPause Pause application after taking screenshot
	 */
	takeScreenshot(doPause: boolean = false):Promise<{[id:string]:string}> {
		return new Promise((resolve) => {
			let reportData = {}; // clear old data
			reportData['render'] = this.getCurrentFrame();
			let $imgRen = this.$('#screenshotRender');
			let $imgSrc = $imgRen;
			$imgRen.attr('src', reportData['render']);

			let video = this.getCurrentVideo();
			if (video) {
				reportData['video'] = video;
				let $imgVid = this.$('#screenshotVideo');
				$imgSrc = $imgVid;
				$imgVid.attr('src', reportData['video']);
			}
			this.pause();
			// size is 0 immediately after image is loaded - we need to wait...
			setTimeout(() => {
				let imgSrc = <HTMLImageElement>$imgSrc.get(0);
				console.info(`ratio: ${imgSrc.naturalWidth}/${imgSrc.naturalHeight} = ${Math.round(imgSrc.naturalWidth * 100 / imgSrc.naturalHeight)}%'`);
				this.$('#screenshotStack').css({
					"padding-top": Math.round(imgSrc.naturalHeight * 100 / imgSrc.naturalWidth)+'%'
				});
			}, 100);
			resolve(reportData);
		});
	}

	/**
	 * @hidden
	 * Pauses render loop
	 */
	pause(){
		this.active = false;
	}

	/**
	 * @hidden
	 * Resumes render loop (after taking screenshot)
	 */
	resume(){
		if (this.active)
			return;
		this.active = true;
		this.renderLoop();
	}
}



// WEBPACK FOOTER //
// ./src/script/app3D/App3D.ts